import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowRightIcon, SparklesIcon, ShieldCheckIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { getProductDownloadLink } from '../utils/productLinks';
import { selectIsProductPurchased } from '../store/store';

const ProductCard = ({ product }) => {
  const { id, name, price, image, shortDescription, category } = product;
  const navigate = useNavigate();
  const isProductPurchased = useSelector(state => selectIsProductPurchased(state, parseInt(id)));

  const handleViewDetails = (e) => {
    e.stopPropagation();
    navigate(`/product/${id}`);
  };

  const handleCardClick = () => {
    navigate(`/product/${id}`);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    const downloadLink = getProductDownloadLink(id);
    if (downloadLink) {
      window.open(downloadLink, '_blank');
    } else {
      toast.error('Download link not available. Please contact support.');
    }
  };

  return (
    <div 
      onClick={handleCardClick}
      className="card flex flex-col h-full cursor-pointer bg-white group"
    >
      {/* Brutalist Category Badge */}
      <div className="absolute top-2.5 left-2.5 sm:top-4 sm:left-4 z-10">
        <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-primary border-2 border-black text-black shadow-[2px_2px_0px_0px_#000000]">
          <SparklesIcon className="h-3 w-3 sm:h-4 sm:w-4" />
          {category}
        </span>
      </div>

      {/* Purchased Badge */}
      {isProductPurchased && (
        <div className="absolute top-2.5 right-2.5 sm:top-4 sm:right-4 z-10">
          <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-black border-2 border-black text-primary shadow-[2px_2px_0px_0px_#9FE240]">
            <ArrowDownTrayIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            Purchased
          </span>
        </div>
      )}

      {/* Image Container */}
      <div className="relative aspect-[16/10] sm:aspect-[4/3] border-b-2 border-black bg-gray-200">
        <img
          src={image}
          alt={name}
          className="object-cover w-full h-full filter contrast-125 saturate-150 grayscale-0 group-hover:grayscale transition-all duration-300"
        />
        
        {/* Hover Info */}
        <div className="absolute inset-0 flex items-end opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          <div className="w-full p-2 sm:p-3 bg-black border-t-2 border-black text-white font-bold uppercase tracking-wide flex justify-between text-xs sm:text-sm">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span>Verified</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-primary">
              <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Premium</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Container */}
      <div className="relative p-3 sm:p-5 sm:p-6 flex flex-col flex-grow bg-white">
        {/* Title */}
        <h3 className="text-sm sm:text-xl font-black text-black uppercase tracking-tight mb-1.5 sm:mb-3 line-clamp-1 sm:line-clamp-2 leading-tight">
          {name}
        </h3>

        {/* Description */}
        <p className="text-gray-800 text-[11px] sm:text-sm line-clamp-2 sm:line-clamp-3 mb-2 sm:mb-5 font-medium flex-grow border-l-3 sm:border-l-4 border-primary pl-2 sm:pl-3 bg-gray-100 py-1 sm:py-2">
          {shortDescription}
        </p>

        {/* Price and Action */}
        <div className="flex items-center justify-between pt-2.5 sm:pt-4 border-t-2 border-black mt-auto">
          <span className="text-base sm:text-2xl font-black text-black" style={{textShadow: '2px 2px 0px #9FE240'}}>
            ₹{price.toLocaleString('en-IN')}
          </span>
          
          {/* Action Button */}
          {isProductPurchased ? (
            <button 
              onClick={handleDownload}
              className="btn bg-black text-primary border-2 border-black shadow-[2px_2px_0px_0px_#9FE240] sm:shadow-[3px_3px_0px_0px_#9FE240] hover:-translate-y-0.5 hover:-translate-x-0.5 flex items-center gap-1 px-2.5 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm font-black"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>Download</span>
            </button>
          ) : (
            <button 
              onClick={handleViewDetails}
              className="btn btn-primary flex items-center gap-1 px-2.5 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm font-black shadow-[2px_2px_0px_0px_#000000] sm:shadow-[3px_3px_0px_0px_#000000]"
            >
              <span>Details</span>
              <ArrowRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 font-bold" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;